/* eslint-disable @tencent/i18next/no-literal-string */
/**
 * 缓存状态管理
 */
import { JSApi } from '@tencent/tdoc-database';
import i18n from '@i18next/i18n';
import type { ILogger, ISnackbar } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IOfflineEditContext } from '../core/interfaces/offline-edit-context.interace';
import { IOfflineDBService } from '../core/interfaces/offline-db-service.interface';
import { IOfflineCacheService } from '../core/interfaces/offline-cache-service.interface';
import { MAX_MANUAL_COUNT } from '../constants';
import { PadIdType } from '../models/padInfo';

const MODULE = '[offline-edit-cache-service]';

export class OfflineCacheService implements IOfflineCacheService {
    public constructor(
        private context: IOfflineEditContext,
        private DBService: IOfflineDBService,
        private logger: ILogger,
        protected snackbar: ISnackbar,
    ) {}

    /**
     * 是否已设置手动离线
     */
    public isManualOffline = async (): Promise<boolean> => {
        const curPadInfo = await this.DBService.getPadInfo(this.context.secretId, PadIdType.SECRET);
        return !!curPadInfo?.isManualOffline;
    };

    /**
     * 切换文档离线状态
     */
    public toggleIsManualOffline = async (): Promise<boolean> => {
        const isManualOffline = await this.isManualOffline();
        if (isManualOffline) {
            // 取消缓存
            const cancelRes = await this.cancelManualOffline();
            return !!cancelRes;
        }
        // 设置缓存
        const setRes = await this.setManualOffline();
        return !!setRes;
    };

    private get docUrl(): string {
        return window.location.href.split('?')[0];
    }

    /**
     * 去掉手动离线标志
     */
    private cancelManualOffline = async (): Promise<boolean> => {
        this.logger.info(MODULE, 'cancelManualOffline ready to getAllManualOfflinePadInfo');
        const result = await this.handlerUpdateIsManualOffline(false);
        this.logger.info(MODULE, 'cancelManualOffline handlerUpdateIsManualOffline result', result);
        JSApi.invokeNative('docx', 'removeManualSaveDoc', { docUrl: this.docUrl }, () => {});
        this.notifyNative(2);
        return result;
    };

    /**
     * 设置手动离线标志
     */
    private setManualOffline = async (): Promise<boolean> => {
        this.logger.info(MODULE, 'ready to getAllManualOfflinePadInfo');
        const allManualPadInfo = await this.DBService.getAllManualOfflinePadInfo();
        if (allManualPadInfo.error) {
            this.logger.error(MODULE, 'getAllManualOfflinePadInfo', allManualPadInfo.error);
            this.snackbar.show({
                message: i18n.t('缓存失败，请稍后重试'),
                type: 'error',
            });
            return false;
        }
        if (allManualPadInfo.data?.length >= MAX_MANUAL_COUNT) {
            this.snackbar.show({
                message: i18n.t('已达到上限，请取消其他缓存的文档后再试。'),
                type: 'error',
            });
            return false;
        }
        const updateRes = await this.handlerUpdateIsManualOffline(true);
        this.logger.info(MODULE, 'setManualOffline handlerUpdateIsManualOffline result', updateRes);
        JSApi.invokeNative('docx', 'manualSaveDocData', { docUrl: this.docUrl }, () => {});
        this.notifyNative(1);
        return updateRes;
    };

    private notifyNative = (status: number): void => {
        JSApi.invokeNative('docx', 'notifyNative', {
            status,
            url: this.docUrl,
            retCode: 0,
            globalPadId: this.context.globalPadId,
            source: 'edit',
        }, () => {});
    };

    /**
     * 更新手动离线标志
     * @param isManualOffline manualOffline
     */
    private handlerUpdateIsManualOffline = async (isManualOffline: boolean): Promise<boolean> => {
        const updateResult = await this.DBService.updateIsManualOffline(isManualOffline);
        if (updateResult.error) {
            this.snackbar.show({
                message: i18n.t(isManualOffline ? '缓存失败，请稍后重试' : '取消缓存失败，请稍后重试。'),
                type: 'error',
            });
            return true;
        }
        this.snackbar.show({
            message: i18n.t(isManualOffline ? '成功缓存该文档' : '已取消缓存该文档'),
            type: 'success',
        });
        return false;
    };
}
